import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getSalesReport, getProductionReport } from '../../../services/reportsService';
import { marketsApi } from '../../../services/api';
import './Reports.css';

const CAT_LABELS = {
  kombucha: 'Kombucha', waterKefir: 'Water Kefir', switchel: 'Switchel',
  fermentedVeg: 'Fermented Veg', brines: 'Brines', mustard: 'Mustard', garlicDip: 'Garlic Dip'
};

const CAT_ORDER = ['ar_frozen','ar_chicken','ar_pork','ar_refrigerated','cc_veg','cc_brine','cc_bev','cc_keg'];
const CAT_NAMES = {
  ar_frozen:'Frozen & Prepared', ar_chicken:'Chicken', ar_pork:'Pork',
  ar_refrigerated:'Refrigerated', cc_veg:'Fermented Veg', cc_brine:'Brines',
  cc_bev:'Kombucha & Beverages', cc_keg:'Kegs'
};

export default function Reports() {
  const [activeTab, setActiveTab] = useState('sales');
  const [markets, setMarkets] = useState([]);

  // Sales filters
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [salesMarket, setSalesMarket] = useState('All');
  const [salesCompany, setSalesCompany] = useState('All');
  const [salesResults, setSalesResults] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);

  // Production filters
  const [prodFrom, setProdFrom] = useState('');
  const [prodTo, setProdTo] = useState('');
  const [prodCategory, setProdCategory] = useState('All');
  const [prodResults, setProdResults] = useState(null);
  const [prodLoading, setProdLoading] = useState(false);

  useEffect(() => {
    marketsApi.getAll().then(res => setMarkets((res.markets || []).filter(m => m.is_active)));
  }, []);

  async function runSalesReport() {
    setSalesLoading(true);
    setSalesResults(null);
    const res = await getSalesReport({
      dateFrom: salesFrom || undefined,
      dateTo: salesTo || undefined,
      marketId: salesMarket !== 'All' ? salesMarket : undefined,
      company: salesCompany !== 'All' ? salesCompany : undefined,
    });
    if (res.success) setSalesResults(res.results);
    else alert('Error: ' + res.error);
    setSalesLoading(false);
  }

  async function runProductionReport() {
    setProdLoading(true);
    setProdResults(null);
    const res = await getProductionReport({
      dateFrom: prodFrom || undefined,
      dateTo: prodTo || undefined,
      category: prodCategory !== 'All' ? prodCategory : undefined,
    });
    if (res.success) setProdResults(res);
    else alert('Error: ' + res.error);
    setProdLoading(false);
  }

  function exportSales() {
    if (!salesResults) return;
    const rows = [['Product', 'Company', 'Category', 'Total Sold', 'Total Packed', 'Market Days']];
    salesResults.forEach(r => rows.push([r.product_name, r.company, CAT_NAMES[r.category] || r.category, r.qty_sold, r.qty_packed, r.market_days]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Sales Report');
    XLSX.writeFile(wb, `sales_report_${salesFrom||'all'}_${salesTo||'all'}.xlsx`);
  }

  function exportProduction() {
    if (!prodResults) return;
    const rows = [['Category', 'Flavor', 'Batches', 'Total Volume', 'Total Cans', 'Total Kegs', 'Total Jars']];
    prodResults.results.forEach(r => rows.push([CAT_LABELS[r.category]||r.category, r.flavor, r.batches, r.totalVolume, r.totalCans, r.totalKegs, r.totalJars]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Production Report');
    XLSX.writeFile(wb, `production_report_${prodFrom||'all'}_${prodTo||'all'}.xlsx`);
  }

  // Group sales results by category
  const groupedSales = salesResults ? CAT_ORDER.reduce((acc, cat) => {
    const items = salesResults.filter(r => r.category === cat);
    if (items.length) acc.push({ cat, label: CAT_NAMES[cat] || cat, items });
    return acc;
  }, []) : [];

  const totalSold = salesResults?.reduce((s, r) => s + r.qty_sold, 0) || 0;
  const totalPacked = salesResults?.reduce((s, r) => s + r.qty_packed, 0) || 0;

  return (
    <div className="rp-container">
      <div className="rp-header">
        <h1>📊 Reports</h1>
      </div>

      <div className="rp-tabs">
        {['sales', 'production'].map(tab => (
          <button key={tab} className={`rp-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'sales' ? '🛒 Sales' : '🏭 Production'}
          </button>
        ))}
      </div>

      {activeTab === 'sales' && (
        <div>
          <div className="rp-filters">
            <div className="rp-filter-group">
              <label>From</label>
              <input type="date" value={salesFrom} onChange={e => setSalesFrom(e.target.value)} />
            </div>
            <div className="rp-filter-group">
              <label>To</label>
              <input type="date" value={salesTo} onChange={e => setSalesTo(e.target.value)} />
            </div>
            <div className="rp-filter-group">
              <label>Market</label>
              <select value={salesMarket} onChange={e => setSalesMarket(e.target.value)}>
                <option value="All">All Markets</option>
                {markets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="rp-filter-group">
              <label>Company</label>
              <select value={salesCompany} onChange={e => setSalesCompany(e.target.value)}>
                <option value="All">AR + CC</option>
                <option value="AR">Apple Ridge</option>
                <option value="CC">Collective Culture</option>
              </select>
            </div>
            <button className="rp-btn-primary" onClick={runSalesReport} disabled={salesLoading}>
              {salesLoading ? 'Loading...' : '▶ Run Report'}
            </button>
            {salesResults && (
              <button className="rp-btn-secondary" onClick={exportSales}>⬇ Export</button>
            )}
          </div>

          {salesResults && (
            <div>
              <div className="rp-summary-cards">
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Units Sold</div>
                  <div className="rp-summary-value">{totalSold.toFixed(0)}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Units Packed</div>
                  <div className="rp-summary-value">{totalPacked.toFixed(0)}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Products Tracked</div>
                  <div className="rp-summary-value">{salesResults.length}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Sell-Through Rate</div>
                  <div className="rp-summary-value">{totalPacked > 0 ? ((totalSold/totalPacked)*100).toFixed(1) + '%' : '—'}</div>
                </div>
              </div>

              {groupedSales.map(({ cat, label, items }) => (
                <div key={cat} className="rp-category-section">
                  <div className="rp-category-header">{label}</div>
                  <table className="rp-table">
                    <thead>
                      <tr><th>Product</th><th>Co.</th><th>Sold</th><th>Packed</th><th>Market Days</th><th>Avg/Day</th><th>Sell-Through</th></tr>
                    </thead>
                    <tbody>
                      {items.map(r => (
                        <tr key={r.product_id}>
                          <td className="rp-td-name">{r.product_name}</td>
                          <td><span className={`rp-co-badge ${r.company?.toLowerCase()}`}>{r.company}</span></td>
                          <td className="rp-td-num">{r.qty_sold.toFixed(0)}</td>
                          <td className="rp-td-num">{r.qty_packed.toFixed(0)}</td>
                          <td className="rp-td-num">{r.market_days}</td>
                          <td className="rp-td-num">{r.market_days > 0 ? (r.qty_sold/r.market_days).toFixed(1) : '—'}</td>
                          <td className="rp-td-num">{r.qty_packed > 0 ? ((r.qty_sold/r.qty_packed)*100).toFixed(0) + '%' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'production' && (
        <div>
          <div className="rp-filters">
            <div className="rp-filter-group">
              <label>From</label>
              <input type="date" value={prodFrom} onChange={e => setProdFrom(e.target.value)} />
            </div>
            <div className="rp-filter-group">
              <label>To</label>
              <input type="date" value={prodTo} onChange={e => setProdTo(e.target.value)} />
            </div>
            <div className="rp-filter-group">
              <label>Category</label>
              <select value={prodCategory} onChange={e => setProdCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <button className="rp-btn-primary" onClick={runProductionReport} disabled={prodLoading}>
              {prodLoading ? 'Loading...' : '▶ Run Report'}
            </button>
            {prodResults && (
              <button className="rp-btn-secondary" onClick={exportProduction}>⬇ Export</button>
            )}
          </div>

          {prodResults && (
            <div>
              <div className="rp-summary-cards">
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Batches</div>
                  <div className="rp-summary-value">{prodResults.rawBatches.length}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Gallons</div>
                  <div className="rp-summary-value">{prodResults.results.reduce((s,r)=>s+r.totalVolume,0).toFixed(0)}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Cans</div>
                  <div className="rp-summary-value">{prodResults.results.reduce((s,r)=>s+r.totalCans,0).toFixed(0)}</div>
                </div>
                <div className="rp-summary-card">
                  <div className="rp-summary-label">Total Jars</div>
                  <div className="rp-summary-value">{prodResults.results.reduce((s,r)=>s+r.totalJars,0).toFixed(0)}</div>
                </div>
              </div>

              <table className="rp-table">
                <thead>
                  <tr><th>Category</th><th>Flavor</th><th>Batches</th><th>Volume (gal)</th><th>Cans</th><th>Kegs</th><th>Jars</th></tr>
                </thead>
                <tbody>
                  {prodResults.results.map((r, i) => (
                    <tr key={i}>
                      <td>{CAT_LABELS[r.category] || r.category}</td>
                      <td className="rp-td-name">{r.flavor || '—'}</td>
                      <td className="rp-td-num">{r.batches}</td>
                      <td className="rp-td-num">{r.totalVolume > 0 ? r.totalVolume.toFixed(1) : '—'}</td>
                      <td className="rp-td-num">{r.totalCans > 0 ? r.totalCans : '—'}</td>
                      <td className="rp-td-num">{r.totalKegs > 0 ? r.totalKegs : '—'}</td>
                      <td className="rp-td-num">{r.totalJars > 0 ? r.totalJars : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
